import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { updateUser } from '@/redux/userSlice'
import athleteApi from '@/lib/api'
import { isMobileDevice, isStandalone } from '@/hooks/useInstallApp'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import PhoneGate from '@/pages/PhoneGate'

const ProtectedRoute: React.FC = () => {
  const { athlete: currentUser, isLoading: loading } = useAuth()
  const location = useLocation()
  const dispatch = useDispatch()

  // Opened from the home screen: record the install once (the "Add to home screen" quest)
  const needsInstallRecord =
    !!currentUser?.id && !currentUser.app_installed_at && isStandalone() && isMobileDevice()
  useEffect(() => {
    if (!needsInstallRecord) return
    athleteApi
      .post('/athlete/app-installed')
      .then((res) => dispatch(updateUser({ app_installed_at: res.data?.app_installed_at })))
      .catch(() => {})
  }, [needsInstallRecord, dispatch])

  // During onboarding, check in once per local day so "Day N of 7" counts the days the athlete
  // actually came back (see recordOnboardingActivity on the server)
  const onboardingUserId = currentUser && !currentUser.welcome_completed ? currentUser.id : null
  useEffect(() => {
    if (!onboardingUserId) return
    const d = new Date()
    const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const key = `athios:activity:${onboardingUserId}:${localDate}`
    try {
      if (sessionStorage.getItem(key)) return
    } catch {}
    athleteApi
      .post('/athlete/activity', { localDate })
      .then((res) => {
        try {
          sessionStorage.setItem(key, '1')
        } catch {}
        dispatch(updateUser({ onboarding_active_days: res.data?.onboarding_active_days }))
      })
      .catch(() => {})
  }, [onboardingUserId, dispatch])

  // Blank only while we don't know who the user is yet; never on a background refresh
  if (loading && !currentUser) {
    return <div></div>
  }
  if (!currentUser || !currentUser.id) {
    return <Navigate to="/signin" replace />
  }

  // Production requires a verified phone before anything else, including the welcome walkthrough
  if (currentUser.phone_verification_required) {
    return <PhoneGate />
  }

  // Redirect authenticated users away from sign-in page
  if (location.pathname === '/signin') {
    return <Navigate to="/" replace />
  }

  const path = location.pathname

  // An athlete who joined from the website has an account but no password yet. Someone who
  // signed in with Google doesn't need one (the server clears this flag for them).
  const needsPassword = currentUser.password_setup_required ?? currentUser.has_password === false
  if (needsPassword) {
    return path === '/start' ? <Outlet /> : <Navigate to="/start" replace />
  }
  // School, date of birth and Terms acceptance come before anything else
  if (currentUser.terms_required || currentUser.school_required) {
    return path === '/setup/terms' ? <Outlet /> : <Navigate to="/setup/terms" replace />
  }
  // Once these are done the whole app is open: offers, deals, messages. A new athlete's first
  // visit to Home shows the welcome once (it marks itself seen); links straight to an offer
  // or a deal go there directly.
  const welcomed = !!currentUser.welcome_completed
  if (path === '/setup/terms' || path === '/start') {
    return <Navigate to={welcomed ? '/' : '/welcome'} replace />
  }
  if (!welcomed && path === '/') {
    return <Navigate to="/welcome" replace />
  }

  return <Outlet />
}

export default ProtectedRoute
